import { getUserImgSrc } from '../../utils/misc.tsx'
import { Spacer } from '~/components/ui/spacer.tsx'
import { Card } from '~/components/ui/card.tsx'
import { Button } from '~/components/ui/inputs/button/Button.tsx'
import { StarsRating } from '~/components/ui/stars.tsx'
import { ScrollArea } from '~/components/ui/scroll-area.tsx'
import { ReviewCard } from '~/features/reviews/review-card.tsx'
import { Icon } from '~/components/ui/icons/icon.tsx'
import { VideoCard } from '~/components/cards/video-card.tsx'
import { Carousel } from '~/components/ui/carousel.tsx'
import FAQ from '~/components/ui/faq.tsx'
import { Breadcrumbs } from '~/components/ui/breadcrumbs.tsx'
import { useEffect, useMemo, useState } from 'react'
import { Form, Link, Outlet, useLocation } from '@remix-run/react'
import { type ReviewWithReviewerId } from '~/features/reviews/getStarsAndReviewDataForProfesional.tsx'
import {
  type Category,
  type Language,
  type Service,
  type Review,
  type User,
  type Video,
  type Session,
  type UserImage,
  type Currency,
} from '@prisma/client'
import { Price } from '~/components/price.tsx'
import { ActionTypeEnum } from '../../../types/enums.ts'
import { ActionDelete } from '~/features/admin/action-delete.tsx'
import ViewPresentationDialog from '~/components/dialogs/view-presentation-dialog.tsx'

export interface FullProfessional extends User {
  services: ServiceWithSessionAndReview[]
  avatarImage?: UserImage
  expertise: Category[]
  favoritedBy: User[]
  languages: Language[]
  video: Video[]
}

export interface ServiceWithSessionAndReview extends Service {
  serviceBooked: SessionWithReview[]
  category: {
    id: number
    name: string
    isEnabled: boolean
  }
}

export interface SessionWithReview extends Session {
  review?: Review
}
type Props = {
  professional: FullProfessional
  stars: number
  latestReviews: ReviewWithReviewerId[]
  reviewsCount: number
  reviewers: Pick<User, 'id' | 'firstName'>[]
  isFavorite: 0 | User
  currency: {
    name: Currency['name']
    value: Currency['value']
  }
}

export const scrollToReviewsSection = (reviewsAnchorId: string) => {
  const reviewsSection = document.getElementById(reviewsAnchorId)
  reviewsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export const ProfessionalProfile = ({
  professional,
  stars,
  latestReviews,
  reviewsCount,
  reviewers,
  isFavorite,
  currency,
}: Props) => {
  const location = useLocation()
  const normalizedSearch = location.search === '?from=' ? '' : location.search

  const lastRoute = location.search
    .replace('?from=', '')
    .replace('&stars', '')
    .replace('&', '?')
  const [lastRoutePath, lastRouteQuery] = lastRoute.split('?')
  const mappedRoutePath =
    lastRoutePath === 'services'
      ? 'servicios'
      : lastRoutePath === 'professionals'
        ? 'profesionales'
        : lastRoutePath
  const homeLink = mappedRoutePath
    ? `/${mappedRoutePath}${lastRouteQuery ? `?${lastRouteQuery}` : ''}`
    : '/'

  const isFromStars: boolean = location.search.includes('&stars')

  const activeServices = professional.services
    .filter(
      service =>
        service.status === 'active' && service.category.isEnabled === true,
    )
    .sort((a, b) => {
      if (a.shownPosition === null || b.shownPosition === null) {
        return 0
      }
      return a.shownPosition - b.shownPosition
    })

  const [showPresentation, setShowPresentation] = useState(false)
  const reviewsAnchorId = 'professional-reviews-section'

  const nonPresentationVideos = useMemo(() => {
    return professional.video
      .filter(video => !video.isPresentation)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
  }, [professional.video])

  let languages = ''
  professional.languages.forEach((language, i) => {
    languages += language.name

    if (professional.languages.length > i + 1) {
      languages += ', '
    }
  })

  const presentation = professional.video.find(video => video.isPresentation)

  useEffect(() => {
    if (!isFromStars || latestReviews.length === 0) {
      return
    }
    const reviewsSection = document.getElementById(reviewsAnchorId)
    reviewsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [isFromStars, latestReviews.length])

  return (
    <div className="w-full min-w-0 overflow-x-hidden pt-2">
      <Breadcrumbs
        className="mb-1 flex items-center gap-2 text-base text-foreground sm:text-lg"
        items={[
          { label: 'Inicio', to: homeLink },
          {
            label: professional.nickname ?? '',
            to: `/${professional.slug}${normalizedSearch}`,
          },
        ]}
      />

      <Spacer size="4xs" />
      <Card className="w-full min-w-0 overflow-hidden p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="flex flex-col items-center gap-6">
            <img
              className="h-[84px] w-[84px] rounded-full object-cover sm:h-[100px] sm:w-[100px]"
              alt={`${professional.nickname}`}
              src={getUserImgSrc(professional.avatarImage?.id)}
            />
            <Form method="POST">
              {isFavorite ? (
                <ActionDelete
                  id={`${professional.nickname}`}
                  trigger={
                    <Button
                      className="whitespace-nowrap border-secondary font-normal text-white"
                      variant={'orange'}
                    >
                      <Icon
                        name="heart-filled"
                        size="drawer"
                        className="text-white"
                      >
                        <span>Eliminar de favoritos</span>
                      </Icon>
                    </Button>
                  }
                  description={`Confirma que desea remover "${professional.nickname}" de sus favoritos?`}
                />
              ) : (
                <Button
                  className="whitespace-nowrap"
                  variant={'outline-secondary'}
                >
                  <input
                    name="type"
                    type="hidden"
                    value={ActionTypeEnum.edit}
                  />
                  <Icon name="heart" size="drawer" className="text-secondary">
                    <span>Añadir a favoritos</span>
                  </Icon>
                </Button>
              )}
            </Form>
          </div>
          <div className="flex w-full min-w-0 flex-col gap-2">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="break-words text-h5">{professional.nickname}</h2>
              {presentation && (
                <div>
                  <Button
                    size={'sm'}
                    className="whitespace-nowrap"
                    variant={'cancel'}
                    onClick={() => setShowPresentation(true)}
                  >
                    <Icon name="play-circle" size="drawer">
                      Ver presentación
                    </Icon>
                  </Button>

                  <ViewPresentationDialog
                    presentation={presentation}
                    open={showPresentation}
                    onOpenChange={isOpen => setShowPresentation(isOpen)}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1 sm:flex-row">
              <p className="text-primary">Calificación promedio: </p>
              <div className="flex flex-wrap gap-1">
                <StarsRating value={stars} />
                {reviewsCount > 0 ? (
                  <Link
                    to={`#${reviewsAnchorId}`}
                    className="text-foreground underline hover:text-secondary"
                    onClick={event => {
                      event.preventDefault()
                      scrollToReviewsSection(reviewsAnchorId)
                    }}
                  >
                    {reviewsCount} valoraciones
                  </Link>
                ) : (
                  <span>0 valoraciones</span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              <p className="text-primary">Idiomas: </p>
              <p className="break-words text-secondary">{languages}</p>
            </div>
            <Spacer size="5xs" />
            <ScrollArea className="max-h-[200px] overflow-auto text-foreground">
              {professional.myResume}
            </ScrollArea>
          </div>
        </div>
        <hr className="my-12 border-input" />

        <section id="professional-services-section">
          <h3 className="mb-7 text-h5">Mis servicios</h3>
          <Spacer size="5xs" />
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            {activeServices.map(service => (
              <div
                key={service.id}
                className="flex w-full min-w-0 flex-col gap-4 rounded-lg border border-input bg-white p-4 lg:gap-5 lg:p-5 xl:flex-row xl:items-stretch xl:gap-6 xl:p-6"
              >
                <div className="w-full min-w-0 xl:min-h-0 xl:flex-1">
                  <div className="mb-4 inline-flex min-h-[20px] max-w-full items-center justify-center rounded-[8px] border border-secondary-300 bg-secondary-200 px-5 py-[2px] text-sm font-bold text-secondary sm:px-8 sm:text-[16px]">
                    {service.category.name}
                  </div>
                  <h4 className="text-h5 leading-tight sm:text-h4">
                    {service.name}
                  </h4>
                  <Spacer size="5xs" />
                  <ScrollArea className="max-h-[140px] min-h-0 overflow-auto pr-2">
                    <p className="break-words text-sm text-foreground-muted sm:text-base">
                      {service.description}
                    </p>
                  </ScrollArea>
                </div>

                <div className="hidden xl:block xl:self-stretch">
                  <div className="h-full min-h-full w-px bg-input" />
                </div>

                <div className="flex w-full min-w-0 flex-col gap-4 xl:w-max xl:shrink-0 xl:justify-between xl:self-stretch xl:pr-3 xl:pt-5">
                  <div className="flex flex-col gap-2 text-secondary">
                    <Icon name="credit-card" size="font">
                      <Price
                        className="p-0 text-sm font-semibold text-secondary sm:text-base"
                        amount={service.price * currency.value}
                        currency={currency.name}
                        rounded={true}
                        isSessionValue={true}
                      />
                    </Icon>
                    <Icon
                      name={service.isVideoCall ? 'video' : 'file-text'}
                      size="font"
                    >
                      {service.isVideoCall ? (
                        <span className="text-sm font-semibold leading-tight sm:text-base">
                          Videollamada
                        </span>
                      ) : (
                        <>
                          <span className="text-sm font-semibold leading-tight sm:text-base xl:hidden">
                            Documento / Audio
                          </span>
                          <span className="hidden flex-col text-sm font-semibold leading-tight sm:text-base xl:flex">
                            <span>Documento</span>
                            <span>Audio</span>
                          </span>
                        </>
                      )}
                    </Icon>
                    {service.isVideoCall && (
                      <Icon name="clock" size="font">
                        <span className="text-sm font-semibold leading-tight sm:text-base">
                          {service.duration} minutos
                        </span>
                      </Icon>
                    )}
                  </div>

                  <Link
                    to={`/${professional.slug}/${service.slug}${location.search}`}
                    className="w-full"
                  >
                    <Button size="lg" className="w-full">
                      Reservar
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {latestReviews.length > 0 && (
          <>
            <Spacer size="3xs" />
            <section id={reviewsAnchorId}>
              <h3 className="mb-7 text-h5">Mis valoraciones</h3>
              <Spacer size="5xs" />
              <Carousel
                slides={latestReviews.map(review => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    user={
                      reviewers.find(
                        reviewer => reviewer.id === review.reviewerId,
                      ) as any
                    }
                  />
                ))}
                withDesktopNavigation
                slidesPerView={1.1}
                spaceBetween={16}
                loop={true}
                breakpoints={{
                  380: {
                    slidesPerView: 1.4,
                  },
                  768: {
                    slidesPerView: 2.2,
                  },
                  960: {
                    slidesPerView: 2.6,
                  },
                  1400: {
                    slidesPerView: 3.2,
                  },
                }}
              />
            </section>
          </>
        )}

        {nonPresentationVideos.length > 0 && (
          <>
            <Spacer size="3xs" />
            <section id="professional-videos-section">
              <h3 className="mb-7 text-h5">Mis videos</h3>
              <Spacer size="5xs" />
              <Carousel
                slides={nonPresentationVideos.map(video => (
                  <VideoCard key={video.id} video={video} />
                ))}
                slidesPerView={1.1}
                spaceBetween={16}
                loop={true}
                breakpoints={{
                  380: {
                    slidesPerView: 1.4,
                  },
                  768: {
                    slidesPerView: 2.2,
                  },
                  960: {
                    slidesPerView: 2.6,
                  },
                  1400: {
                    slidesPerView: 3.2,
                  },
                }}
              />
            </section>
          </>
        )}

        <Spacer size="3xs" />
        <section id="professional-faq-section">
          <h3 className="text-h5">Preguntas frecuentes</h3>
          <Spacer size="5xs" />
          <div className="max-w-[760px]">
            <FAQ />
          </div>
        </section>
      </Card>
      <Outlet />
    </div>
  )
}
